// Time estimate constants removed from usage; import deleted.

import {
  hasUploadFailed,
  isStoryCompressedVideoReady,
  isStoryLoading,
  isStoryOriginalVideoReady,
  isStoryTranscriptionReady,
} from './utils';

import type { StoryUploadPhase } from '@src/stores-v2/UserStoryUploadFlowStore';
import type { DashboardStory } from '@src/types.ts/story';

export interface UploadStepInfo {
  stepNumber: number; // 1-based, 6 = complete/final
  totalSteps: number;
  label: string; // short
  detail: string; // longer description
  done: boolean;
  error?: boolean;
}

// Total logical steps (excluding failed): 5 actionable + complete state
const TOTAL_STEPS = 5;

export function deriveUploadStep(
  story: DashboardStory,
  phase: StoryUploadPhase,
): UploadStepInfo {
  if (hasUploadFailed(story)) {
    return {
      stepNumber: 0,
      totalSteps: TOTAL_STEPS,
      label: 'Failed',
      detail: 'Upload failed – please retry',
      done: false,
      error: true,
    };
  }

  if (!isStoryLoading(story)) {
    return {
      stepNumber: TOTAL_STEPS,
      totalSteps: TOTAL_STEPS,
      label: 'Complete',
      detail: 'Ready',
      done: true,
    };
  }

  if (phase.phase === 'idle') {
    return {
      stepNumber: 0,
      totalSteps: TOTAL_STEPS,
      label: 'Pending',
      detail: 'Waiting to start',
      done: false,
    };
  }

  // Phase: uploading video file(s)
  if (phase.phase === 'uploading') {
    return {
      stepNumber: 1,
      totalSteps: TOTAL_STEPS,
      label: 'Uploading Video',
      detail: 'Transferring video file(s)',
      done: false,
    };
  }
  // From here on we are processing
  if (!isStoryOriginalVideoReady(story)) {
    return {
      stepNumber: 2,
      totalSteps: TOTAL_STEPS,
      label: 'Optimizing',
      detail: 'Optimizing original video',
      done: false,
    };
  }
  if (!isStoryTranscriptionReady(story)) {
    return {
      stepNumber: 3,
      totalSteps: TOTAL_STEPS,
      label: 'Transcribing',
      detail: 'Speech to text in progress',
      done: false,
    };
  }
  if (!isStoryCompressedVideoReady(story)) {
    return {
      stepNumber: 4,
      totalSteps: TOTAL_STEPS,
      label: 'Updating Data',
      detail: 'Creating derivatives & assets',
      done: false,
    };
  }
  // All ready but still considered loading (rare race)
  return {
    stepNumber: 5,
    totalSteps: TOTAL_STEPS,
    label: 'Finalizing',
    detail: 'Finishing setup',
    done: false,
  };
}
